import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { useEffect, useState, useCallback } from "react";
import StatsUI from "../molecules/StatsUI";
import MassiveTaskDialog from "./MassiveTaskDialog";
import { Select } from "./Select";
import NavbarFilter from "./NavbarFilter";
import { useTranslation } from "react-i18next";
import { MultiSelect } from "./MultiSelect";
import {
  GenderOption,
  getOptionsStatusBusiness,
  optionsGender,
} from "@/shared/constants/constants";
import { StatItemProps } from "../atoms/StatItemUI";
import { useLazyCatchmentKpiQuery } from "@/app/catchment/catchment.api";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { XMarkIcon } from "@heroicons/react/20/solid";

export type NavbarFilterCatchmentDirectCenterProps = {
  branches: any[];
  campaigns: any[];
  exportData: any;
  optionsTagsMarketing: any[];
  branchId: string;
  setOpenCreateLeaModal: (value: boolean) => void;
  selectedBranch: any;
  selectBranch: (branch: any) => void;
  // Props para manejar el estado del filtro con useState
  filterValues: any;
  onFilterChange: (field: string, value: any) => void;
};

const NavbarFilterDirectCenter: React.FC<
  NavbarFilterCatchmentDirectCenterProps
> = ({
  branches,
  campaigns,
  exportData,
  optionsTagsMarketing,
  branchId,
  setOpenCreateLeaModal,
  selectedBranch,
  selectBranch,
  // Se reciben los valores y el manejador del filtro
  filterValues,
  onFilterChange,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [statsUI, setStatsUI] = useState<StatItemProps[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isKpisOpen, setIsKpisOpen] = useState(false);

  const [trigger, { isLoading, isFetching }] = useLazyCatchmentKpiQuery();

  const optionsMenu = [
    { name: "Tarea masiva", functionMenu: () => setOpen(true) },
  ];
  const optionsStatusBusiness = getOptionsStatusBusiness();

  const toggleDrawer = useCallback(() => setIsDrawerOpen((prev) => !prev), []);
  const toggleKpis = useCallback(() => setIsKpisOpen((prev) => !prev), []);

  const handleBranchChange = (option: any) => {
    selectBranch(option);
    onFilterChange("branch_filter", option.id);
  };

  useEffect(() => {
    const fetchKpiData = async () => {
      if (isKpisOpen) {
        try {
          const result = await trigger({
            branch_filter: filterValues.branch_filter,
            catchment_page: "direct",
          }).unwrap();
          const transformStatsUI: StatItemProps[] = Object.values(result).map(
            (stat: any) => ({
              name: stat?.title,
              stat: stat?.data?.toString(),
            })
          );
          setStatsUI(transformStatsUI);
        } catch (err) {
          console.error("Failed to fetch KPI data:", err);
        }
      }
    };
    fetchKpiData();
  }, [isKpisOpen, filterValues.branch_filter, trigger]);

  return (
    <div className="flex flex-1 self-stretch lg:gap-x-6 px-4 bottom pb-2 border-b-2">
      <NavbarFilter
        formComponents={
          <div className="flex flex-col md:flex-row flex-1 items-center gap-4 w-full">
            <InputWithCornerHint
              label="Nombre"
              id="name"
              name="search"
              type="text"
              className="w-full"
              placeholder="Nombre"
              value={filterValues.search ?? ""}
              onChange={(e) => onFilterChange("search", e.target.value)}
            />
            <Select
              selected={selectedBranch}
              setSelected={handleBranchChange}
              options={branches}
              tittle={t("user.views.branch")}
              name="branch_filter"
            />
          </div>
        }
        exportData={exportData}
        toggleDrawer={toggleDrawer}
        toggleKpis={toggleKpis}
        OnClickPrimaryButton={() =>
          navigate(`/branchs/${branchId}/marketing/captation/tasks`)
        }
        optionsMenu={optionsMenu}
        showButtonAdd={true}
        setToggleButtonAdd={() => setOpenCreateLeaModal(true)}
      />
      <Dialog
        open={isDrawerOpen}
        onClose={toggleDrawer}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black opacity-50" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="bg-white w-full max-w-2xl rounded-2xl shadow-xl">
            <div className="p-4 flex flex-col w-full">
              <div className="flex items-center justify-end h-7">
                <button
                  type="button"
                  onClick={toggleDrawer}
                  className="relative text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                </button>
              </div>
              <form className="grid w-full grid-cols-1 sm:grid-cols-2 gap-4 md:flex-row md:flex-wrap md:gap-x-6">
                <Select
                  options={optionsGender}
                  selected={optionsGender.find(
                    (g) => g.id === filterValues.sex_filter
                  )}
                  setSelected={(option) =>
                    onFilterChange("sex_filter", option?.id)
                  }
                  tittle={t("user.views.sex")}
                  name="sex_filter"
                />
                <Select
                  selected={optionsStatusBusiness.find(
                    (s) => s.id === filterValues.stages_filter
                  )}
                  setSelected={(option) =>
                    onFilterChange("stages_filter", option?.id)
                  }
                  options={optionsStatusBusiness}
                  tittle={t("recovery.views.business_stages")}
                  name="stages_filter"
                />
                <MultiSelect
                  options={optionsTagsMarketing}
                  selectedOptions={optionsTagsMarketing?.filter((tag) =>
                    (filterValues.tag_marketing_filter || []).includes(tag?.id)
                  )}
                  setSelectedOptions={(selectedOptions: any) => {
                    const selectedIds = selectedOptions.map((o) => o.id);
                    onFilterChange("tag_marketing_filter", selectedIds);
                  }}
                  title={t("task.views.labels")}
                />
                <Select
                  selected={campaigns.find(
                    (c) => c.id === filterValues.campaign_filter
                  )}
                  setSelected={(option) =>
                    onFilterChange("campaign_filter", option.id)
                  }
                  options={campaigns}
                  tittle={t("share.layouts.companies")}
                  name="company"
                />
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <Dialog open={isKpisOpen} onClose={toggleKpis} className="relative z-50">
        <DialogBackdrop
          className="fixed inset-0 bg-black opacity-50"
          onClick={toggleKpis}
        />
        <DialogPanel className="bg-white w-full rounded-xl lg:max-w-xl max-h-min top-48 lg:top-22 overflow-y-auto shadow-xl fixed inset-0 mx-auto overflow-hidden">
          {isLoading || isFetching ? (
            <div className="flex justify-center items-center h-20">
              <FontAwesomeIcon icon={faLoader} spin className="text-3xl" />
            </div>
          ) : (
            <StatsUI title="KPIs" stats={statsUI} onClose={toggleKpis} />
          )}
        </DialogPanel>
      </Dialog>

      <MassiveTaskDialog
        setOpen={setOpen}
        open={open}
        optionsBranches={branches}
        optionsStatusBusiness={optionsStatusBusiness}
        optionsTagsMarketing={optionsTagsMarketing}
      />
    </div>
  );
};

export default NavbarFilterDirectCenter;
